import { useState, useRef, useEffect } from "react";
import LegalFooter from "../../components/LegalFooter";
import { auth } from "../../firebase";
import { db } from "../../firebase";
import { saveProfilePhoto, getProfilePhoto } from "../../utils/indexedDB";

import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

export default function Profile({
  sidebarTheme,
  styles,
  isAuthenticated,
  userProfile,
}) {
  const defaultProfile = {
    Name: "",
    DOB: "",
    Age: "",
    Class: "",
    Gender: "",
    Email: "",
    Description: "",
  };
  const footerStyles = {
    sectionTitle: {
      textAlign: "center",
      fontSize: 22,
      fontWeight: 700,
      color: "#a855f7",
      marginBottom: 16,
    },
  };
  const [data, setData] = useState(defaultProfile);

  const [edit, setEdit] = useState(null);
  const [temp, setTemp] = useState("");

  const [classError, setClassError] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const [showClassInfo, setShowClassInfo] = useState(false);

  const [photo, setPhoto] = useState("");

  const fileRef = useRef(null);
  const isMobile = window.matchMedia("(hover: none)").matches;

  // 🔥 SYNC PROFILE FROM APP (SINGLE SOURCE OF TRUTH)
  useEffect(() => {
    if (!userProfile) return;

    setData((prev) => ({
      ...prev,

      // 🔥 normalize for UI
      Name: userProfile.Name || userProfile.name || "",
      DOB: userProfile.DOB || "",
      Age: userProfile.Age || "",
      Class: userProfile.Class || "",
      Gender: userProfile.Gender || "",
      Email: userProfile.Email || "",
      Description: userProfile.Description || "",
    }));
  }, [userProfile]);

  /* ================= LOAD GUEST PROFILE ================= */
  useEffect(() => {
    if (auth.currentUser) return; // logged-in handled by Firestore loader

    const raw = localStorage.getItem("profileData");
    if (!raw) return;

    try {
      const localProfile = JSON.parse(raw);
      setData((prev) => ({ ...prev, ...localProfile }));
    } catch (e) {
      console.error("Invalid local profile data");
    }
  }, []);

  /* ================= LOAD PROFILE ================= */
  useEffect(() => {
    if (!auth.currentUser || userProfile) return;

    const loadProfile = async () => {
      try {
        const uid = auth.currentUser.uid;
        const ref = doc(db, "users", uid, "profile", "main");
        const snap = await getDoc(ref);

        if (snap.exists()) {
          const saved = snap.data();
          setData((prev) => ({ ...prev, ...saved }));
        } else {
          await setDoc(doc(db, "users", uid, "profile", "main"), {
            ...defaultProfile,
            Email: auth.currentUser.email || "",
            createdAt: serverTimestamp(),
          });
        }
      } catch (err) {
        console.error("Failed to load profile:", err);
      }
    };

    loadProfile();
  }, [isAuthenticated]);
  useEffect(() => {
    if (!auth.currentUser) return;

    let photoUrl = null;

    const loadPhoto = async () => {
      const blob = await getProfilePhoto();
      if (blob) {
        photoUrl = URL.createObjectURL(blob);
        setPhoto(photoUrl);
      }
    };

    loadPhoto();

    return () => {
      if (photoUrl) {
        URL.revokeObjectURL(photoUrl);
      }
    };
  }, []);

  /* ================= FORCE AUTH EMAIL ================= */
  useEffect(() => {
    if (!auth.currentUser) return;

    if (auth.currentUser.email) {
      setData((prev) => ({
        ...prev,
        Email: auth.currentUser.email, // 🔒 auth email always wins
      }));
    }
  }, [isAuthenticated]);

  /* ================= AUTO CALCULATE AGE ================= */
  useEffect(() => {
    if (data.DOB) {
      const age = calculateAge(data.DOB);
      if (age && age !== data.Age) {
        setData((prev) => ({
          ...prev,
          Age: age,
        }));
      }
    }
  }, [data.DOB]);

  /* ================= CLEAN INVALID CLASS ================= */
  useEffect(() => {
    if (
      data.Class !== "" &&
      (!Number.isInteger(Number(data.Class)) ||
        Number(data.Class) < 0 ||
        Number(data.Class) > 14)
    ) {
      setData((prev) => ({ ...prev, Class: "" }));
    }
  }, [data.Class]);

  /* ================= HELPERS ================= */
  const formatDateDMY = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  const calculateAge = (dob) => {
    if (!dob) return "";

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= 0 ? age.toString() : "";
  };

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  /* ================= SAVE ================= */
  const save = (key) => {
    if (key === "Age") return;

    if (key === "Class") {
      const v = Number(temp);
      if (!Number.isInteger(v) || v < 0 || v > 14) {
        setClassError(true);
        return;
      }
      setClassError(false);
    }

    if (key === "Email") {
      if (!isValidEmail(temp)) {
        setEmailError(true);
        return;
      }
      setEmailError(false);
    }

    if (key === "Gender" && !temp) return;

    let updated = { ...data, [key]: temp };

    if (key === "DOB") {
      const selected = new Date(temp);
      const today = new Date();
      selected.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      if (selected > today) {
        alert("Date of birth cannot be in the future");
        return;
      }

      updated.Age = calculateAge(temp);
    }

    setData(updated);
    setEdit(null);

    if (auth.currentUser) {
      const uid = auth.currentUser.uid;

      setDoc(
        doc(db, "users", uid, "profile", "main"),
        {
          ...updated,

          updatedAt: serverTimestamp(),
        },
        { merge: true }
      ).catch((err) => {
        console.error("Profile save failed:", err);
      });
    }
  };
  const onPhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const blob = await file.arrayBuffer();
    const photoBlob = new Blob([blob], { type: file.type });

    await saveProfilePhoto(photoBlob);
    setPhoto(URL.createObjectURL(photoBlob));
  };

  return (
    <div>
      {/* PROFILE PHOTO (UNCHANGED) */}
      <h1 style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div
          onMouseEnter={(e) => {
            const pen = e.currentTarget.querySelector(".photo-pen");
            if (pen) pen.style.opacity = 1;
          }}
          onMouseLeave={(e) => {
            const pen = e.currentTarget.querySelector(".photo-pen");
            if (pen) pen.style.opacity = photo ? 0.4 : 0;
          }}
          onClick={() => fileRef.current.click()}
          style={{
            width: 84,
            height: 84,
            borderRadius: "50%",
            background: photo
              ? `url(${photo}) center/cover`
              : "linear-gradient(135deg,#7c3aed,#9333ea)",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {!photo && <span style={{ fontSize: 12 }}>Add Photo</span>}

          <span
            className="photo-pen"
            style={{
              position: "absolute",
              bottom: 6,
              right: 6,
              fontSize: 16,
              background: "rgba(0,0,0,0.6)",
              borderRadius: "50%",
              padding: 6,
              color: "#fff",
              opacity: photo ? 0.4 : 0,
              transition: "0.2s ease",
              pointerEvents: "none",
            }}
          >
            ✏️
          </span>
        </div>
        Profile
        <input
          type="file"
          accept="image/*"
          ref={fileRef}
          onChange={onPhotoChange}
          style={{ display: "none" }}
        />
      </h1>

      {/* PROFILE FIELDS */}
      {Object.keys(data)
        .filter(
          (key) =>
            key !== "Photo" &&
            key !== "photo" &&
            key !== "createdAt" &&
            key !== "updatedAt" &&
            key !== "migratedFromGuest"
        )

        .map((key) => {
          const isDescription = key === "Description"; // ✅ ADD THIS LINE

          return (
            <div
              key={key}
              style={{
                ...styles(sidebarTheme).card,
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                flexDirection: isDescription ? "column" : "row", // 🔥 KEY LINE
                alignItems: isDescription ? "stretch" : "flex-start",
              }}
            >
              <strong
                style={{
                  minWidth: 140,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 6,
                  width: isDescription ? "100%" : "140px",
                }}
              >
                {key === "DOB" ? "Date of Birth" : key}

                {key === "Class" && (
                  <span
                    onMouseEnter={() => !isMobile && setShowClassInfo(true)}
                    onMouseLeave={() => !isMobile && setShowClassInfo(false)}
                    onClick={() => setShowClassInfo((v) => !v)}
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      color: sidebarTheme.accent,
                      boxShadow:
                        sidebarTheme.text === "#0f172a"
                          ? "0 6px 18px rgba(0,0,0,0.12)"
                          : "0 6px 18px rgba(0,0,0,0.4)",
                      border: `1px solid ${sidebarTheme.accent}`,
                      position: "relative",
                    }}
                  >
                    i
                    {showClassInfo && (
                      <div
                        style={{
                          position: "absolute",
                          top: "140%",
                          left: "50%",
                          transform: "translateX(-50%)",

                          /* 🔥 KEY FIXES */
                          display: "inline-flex", // ✅ not block
                          alignItems: "center",
                          gap: 6,
                          whiteSpace: "nowrap", // ✅ force horizontal
                          width: "max-content", // ✅ expand to content
                          maxWidth: "calc(100vw - 24px)", // ✅ screen-safe
                          padding: "8px 12px",

                          marginLeft: 12, // ✅ left gap
                          marginRight: 12, // ✅ right gap

                          fontSize: 12,
                          lineHeight: 1.4,
                          borderRadius: 10,

                          background:
                            sidebarTheme.text === "#0f172a"
                              ? "#ffffff"
                              : "#020617",
                          color:
                            sidebarTheme.text === "#0f172a"
                              ? "#020617"
                              : "#e5e7eb",

                          border: `1px solid ${sidebarTheme.accent}`,
                          boxShadow:
                            sidebarTheme.text === "#0f172a"
                              ? "0 10px 30px rgba(0,0,0,0.12)"
                              : "0 10px 30px rgba(0,0,0,0.45)",

                          zIndex: 9999,
                        }}
                      >
                        0 → UKG/LKG · 1–12 → School · 13 → Dropper · 14 →
                        College
                      </div>
                    )}
                  </span>
                )}
              </strong>
              {edit === key ? (
                key === "Description" ? (
                  <textarea
                    value={temp}
                    onChange={(e) => setTemp(e.target.value)}
                    rows={5}
                    style={{
                      ...styles(sidebarTheme).profileInput,
                      width: "100%",
                    }}
                  />
                ) : key === "DOB" ? (
                  <input
                    type="date"
                    value={temp}
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setTemp(e.target.value)}
                    style={{
                      ...styles(sidebarTheme).profileInput,
                      flex: 1,
                      textAlign: "center",
                    }}
                  />
                ) : key === "Class" ? (
                  <div style={{ flex: 1 }}>
                    <input
                      type="number"
                      value={temp}
                      onChange={(e) => {
                        const v = e.target.value;
                        setTemp(v);
                        setClassError(
                          v === "" ||
                            !Number.isInteger(Number(v)) ||
                            Number(v) < 0 ||
                            Number(v) > 14
                        );
                      }}
                      style={{
                        ...styles(sidebarTheme).profileInput,
                        width: "100%",
                        textAlign: "center",
                        border: classError
                          ? "1.5px solid #ef4444"
                          : styles(sidebarTheme).profileInput.border,
                      }}
                    />
                    {classError && (
                      <div
                        style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}
                      >
                        Class must be between 0 and 14
                      </div>
                    )}
                  </div>
                ) : key === "Email" ? (
                  <div style={{ flex: 1 }}>
                    <input
                      type="email"
                      placeholder="example@email.com"
                      value={temp}
                      onChange={(e) => {
                        setTemp(e.target.value);
                        setEmailError(!isValidEmail(e.target.value));
                      }}
                      style={{
                        ...styles(sidebarTheme).profileInput,
                        width: "100%",
                        textAlign: "center",
                        border: emailError
                          ? "1.5px solid #ef4444"
                          : styles(sidebarTheme).profileInput.border,
                      }}
                    />
                    {emailError && (
                      <div
                        style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}
                      >
                        Enter a valid email address
                      </div>
                    )}
                  </div>
                ) : key === "Gender" ? (
                  <select
                    value={temp}
                    onChange={(e) => setTemp(e.target.value)}
                    style={{
                      ...styles(sidebarTheme).profileInput,
                      flex: 1,
                      textAlignLast: "center",
                      textAlign: "center",
                    }}
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                ) : (
                  <input
                    value={temp}
                    onChange={(e) => setTemp(e.target.value)}
                    disabled={key === "Age"}
                    style={{
                      ...styles(sidebarTheme).profileInput,
                      flex: 1,
                      textAlignLast: "center",
                      textAlign: "center",
                    }}
                  />
                )
              ) : (
                <div
                  style={{
                    flex: 1,
                    opacity: data[key] ? 1 : 0.5,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {key === "Email" ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        opacity: data.Email ? 1 : 0.5,
                      }}
                    >
                      <span
                        style={{
                          wordBreak: "break-all",
                          maxWidth: "100%",
                        }}
                      >
                        {data.Email || "—"}
                      </span>

                      {auth.currentUser?.emailVerified && (
                        <span
                          title="Email verified"
                          style={{
                            color: "#2563eb",
                            fontSize: 14,
                            fontWeight: 700,
                            lineHeight: 1,
                          }}
                        >
                          ✔
                        </span>
                      )}
                    </div>
                  ) : key === "DOB" ? (
                    formatDateDMY(data[key])
                  ) : (
                    data[key] || "—"
                  )}
                </div>
              )}
              {edit !== key &&
                key !== "Age" &&
                !(key === "Email" && isAuthenticated) && (
                  <span
                    style={{
                      ...styles(sidebarTheme).pen,
                      alignSelf: isDescription ? "flex-end" : "center", // ✅ FIX
                      marginTop: isDescription ? "-32px" : 0, // ✅ MOVE UP
                    }}
                    onClick={() => {
                      if (key === "Email" && isAuthenticated) return;
                      setEdit(key);
                      setTemp(data[key]);
                      setClassError(false);
                      setEmailError(false);
                    }}
                  >
                    ✏️
                  </span>
                )}
              {edit === key && (
                <button onClick={() => save(key)} style={{ marginLeft: 8 }}>
                  Save
                </button>
              )}
              {key === "Email" && isAuthenticated && (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end", // ✅ RIGHT ALIGN
                  }}
                >
                  <button
                    onClick={async () => {
                      const { sendPasswordResetEmail } = await import(
                        "firebase/auth"
                      );
                      await sendPasswordResetEmail(
                        auth,
                        auth.currentUser.email
                      );
                      alert("Password reset link sent to your email.");
                    }}
                    style={{
                      marginTop: 8,
                      padding: "6px 14px",
                      fontSize: 12,
                      fontWeight: 700,
                      borderRadius: 999,
                      border: "1px solid rgba(124,58,237,0.6)",
                      background: "rgba(124,58,237,0.12)",
                      color: "#7c3aed",
                      cursor: "pointer",
                      boxShadow: "0 0 12px rgba(124,58,237,0.45)",
                    }}
                  >
                    Reset password
                  </button>
                </div>
              )}
            </div>
          );
        })}
      {/* 🔵 IMPORTANT NOTE */}
      <div
        style={{
          marginTop: 30,
          fontSize: 13,
          fontWeight: 600,
          color: sidebarTheme.text,
          opacity: 0.85,
          lineHeight: 1.6,
        }}
      >
        <div style={{ fontWeight: 800, marginBottom: 6 }}>ℹ️ Important</div>

        <div>
          <strong>✔ Blue tick</strong> indicates a verified account email.
        </div>

        <div style={{ marginTop: 6, opacity: 0.95 }}>
          If you don’t receive verification or password reset email, please
          check your
          <strong> Spam / Junk</strong> folder.
        </div>
      </div>

      {/* FOOTER */}
      <div style={{ marginTop: 80, opacity: 0.9 }}>
        <LegalFooter styles={footerStyles} />
      </div>
    </div>
  );
}
